import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import SchoolDocumentHeader from "@/components/SchoolDocumentHeader";
import { RatingForm, ReportBody, Violations, fmtWeek } from "@/components/followup/shared";
import { btnCls, inputCls } from "@/components/followup/ui";

// صفحة الرابط الخاص: يفتحها المعلم أو الوكيل أو رائد النشاط أو ولي الأمر من رسالة الواتساب بلا حساب.
// المعلم يقيّم بعد كل حصة، ورائد النشاط بعد كل نشاط، والوكيل وولي الأمر يوميًا (تقييم اليوم يُعدَّل إن أعاده).
function ViolationBox({ token, list, onChanged }) {
  const [text, setText] = useState("");
  const [degree, setDegree] = useState("");
  const [busy, setBusy] = useState(false);
  const add = async () => {
    setBusy(true);
    try { await api.post(`/followup/public/${token}/violations`, { text, degree: degree ? Number(degree) : null }); setText(""); setDegree(""); toast.success("سُجّلت المخالفة"); onChanged(); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  const del = async (v) => {
    if (!window.confirm("حذف هذه المخالفة؟")) return;
    try { await api.delete(`/followup/public/${token}/violations/${v.id}`); onChanged(); } catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <section className="bg-white rounded-2xl border border-[#e6e2d8] p-5 space-y-3" data-testid="fu-violation-box">
      <div className="font-bold">تسجيل مخالفة على الطالب</div>
      <p className="text-xs text-[#4A554E]">تظهر المخالفة للمرشد وفي التقرير الأسبوعي. اكتب ما حدث باختصار.</p>
      <textarea className={inputCls} rows={2} maxLength={300} value={text} onChange={(e) => setText(e.target.value)} placeholder="مثال: تأخر عن الطابور وخروج من الصف دون إذن" data-testid="fu-violation-text" />
      <div className="flex flex-wrap gap-2 items-center">
        <select className={`${inputCls} !w-auto`} value={degree} onChange={(e) => setDegree(e.target.value)}>
          <option value="">الدرجة (اختياري)</option>
          {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>الدرجة {n}</option>)}
        </select>
        <button className={btnCls} disabled={!text.trim() || busy} onClick={add} data-testid="fu-violation-add">تسجيل المخالفة</button>
      </div>
      {list.length > 0 && <div><div className="text-sm font-bold mb-1">ما سجّلته هذا الأسبوع</div><Violations list={list} onDelete={del} /></div>}
    </section>
  );
}

export default function FollowRate() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [saved, setSaved] = useState(false);
  const [rep, setRep] = useState(null);
  const load = useCallback(() => api.get(`/followup/public/${token}`).then((r) => setD(r.data)).catch((e) => setErr(formatErr(e))), [token]);
  useEffect(() => { load(); }, [load]);
  const girl = d?.student?.gender === "female";
  const daily = d?.role === "parent" || d?.role === "deputy";
  const todayEntry = daily ? (d?.entries || []).find((e) => e.date === d.today) : null;
  const intro = !d ? "" : d.role === "parent"
    ? `قيّموا ${girl ? "ابنتكم" : "ابنكم"} في المنزل من A إلى D. يمكنكم التقييم كل يوم خلال الأسبوع.`
    : d.role === "deputy" ? "قيّم الطالب من A إلى D في الطابور والفسحة والصلاة. يمكنك التقييم كل يوم خلال الأسبوع."
    : d.role === "activity" ? "قيّم الطالب من A إلى D بعد كل نشاط خلال هذا الأسبوع."
    : "قيّم الطالب من A إلى D بعد كل حصة خلال هذا الأسبوع.";
  const again = d?.role === "parent" || d?.role === "deputy" ? "تعديل تقييم اليوم" : d?.role === "activity" ? "تقييم نشاط آخر" : "تقييم حصة أخرى";
  return (
    <div className="platform-shell min-h-screen p-4" dir="rtl" data-testid="rate-link-page">
      <div className="max-w-xl mx-auto space-y-4">
        {err && <div className="bg-white rounded-2xl border border-[#e6e2d8] p-6 text-center">{err}</div>}
        {d && (
          <>
            <section className="bg-white rounded-2xl border border-[#e6e2d8] p-5 text-center space-y-1">
              <SchoolDocumentHeader schoolKey={d.school_key} gender={d.gender} schoolName={d.school_name} />
              <h1 className="text-xl font-extrabold pt-3">متابعة الطالب</h1>
              <div className="text-lg font-bold text-[#3E6A56]">{d.student.name} <span className="text-sm text-[#4A554E]">{d.student.grade}</span></div>
              <div className="text-sm text-[#4A554E]">أسبوع {fmtWeek(d.week)}</div>
              {d.role !== "parent" && d.rater_name && <div className="text-sm pt-1">{d.rater_name}{d.subjects ? ` — ${d.subjects}` : ""}</div>}
              {d.reason && <div className="text-xs text-[#4A554E]">سبب المتابعة: {d.reason}</div>}
            </section>
            {d.status !== "active" ? (
              <section className="bg-white rounded-2xl border border-[#e6e2d8] p-5 text-center">انتهت متابعة هذا الأسبوع. شكرًا لتعاونكم.</section>
            ) : saved ? (
              <section className="bg-white rounded-2xl border border-[#e6e2d8] p-6 text-center space-y-3" data-testid="rate-thanks">
                <div className="text-2xl">✓</div>
                <div className="font-bold">شكرًا لكم، وصل تقييمكم للمرشد الطلابي{d.counselor ? ` (${d.counselor})` : ""}.</div>
                <button className="underline text-sm" onClick={() => setSaved(false)}>{again}</button>
              </section>
            ) : (
              <section className="bg-white rounded-2xl border border-[#e6e2d8] p-5 space-y-3">
                <p className="text-sm text-[#4A554E]">{intro}</p>
                <RatingForm key={d.entries.length} criteria={d.criteria} initial={todayEntry} withComplaint={d.role === "activity"}
                  saveLabel={daily && todayEntry ? "تعديل تقييم اليوم" : "إرسال التقييم"}
                  onSave={async (body) => {
                    try { await api.post(`/followup/public/${token}`, body); setSaved(true); toast.success("تم حفظ التقييم"); load(); }
                    catch (e) { toast.error(formatErr(e)); }
                  }} />
              </section>
            )}
            {d.role === "deputy" && d.status === "active" && <ViolationBox token={token} list={d.violations || []} onChanged={load} />}
            {d.entries?.length > 0 && (
              <section className="bg-white rounded-2xl border border-[#e6e2d8] p-4 text-sm">
                <b>تقييماتك هذا الأسبوع:</b> {d.entries.length}
                <div className="flex flex-wrap gap-1 mt-2">{d.entries.map((e, i) => <span key={i} className="px-2 py-0.5 rounded-full bg-[#EEF3F2] text-xs">{e.date}</span>)}</div>
              </section>
            )}
            {d.role === "parent" && d.reports?.length > 0 && (
              <section className="bg-white rounded-2xl border border-[#e6e2d8] p-5 space-y-2">
                <div className="font-bold">التقارير الأسبوعية</div>
                {d.reports.map((r) => (
                  <button key={r.id} className="w-full text-start border border-[#e6e2d8] rounded-xl p-3 flex justify-between" onClick={() => setRep(rep?.id === r.id ? null : r)}>
                    <span>أسبوع {fmtWeek(r.week)}</span><b>{r.letter || "—"} {r.pct != null && `· ${r.pct}%`}</b>
                  </button>
                ))}
                {rep && <ReportBody rep={rep} />}
              </section>
            )}
          </>
        )}
        <footer className="text-center text-xs text-[#4A554E] py-6">منصة الإرشاد الطلابي — شركة كيان النموذجية</footer>
      </div>
    </div>
  );
}
